import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

import { AppState } from 'models/app-store';
import { PassengerTypes } from 'models/http/passenger';
import { ModuleActions, ModuleCodes } from 'models/modules';
import Home from 'pages/Home/Home';
import HomeAgent from 'pages/Home/HomeAgent/HomeAgent';
import { JobsViewType } from 'pages/Jobs/JobsCalendar/JobsCalendar.model';
import UserProfile from 'pages/UserProfile/UserProfile';
import { OperationTypes } from 'utils/enums/system-template';
import useOperationTranslation from 'utils/hooks/use-operation-translation';
import usePerm from 'utils/hooks/use-perm';

import guards from '../../routes/guards';
import { GuardData } from '../../routes/guards/guard.model';

type JSXRoute = BaseRoute & {
  jsx: React.ReactNode;
};

type ComponentRoute = BaseRoute & {
  component: React.FC;
};

const Timeline = React.lazy(() => import('pages/Timeline/Timeline'));
const Jobs = React.lazy(() => import('pages/Jobs/Jobs'));
const Trips = React.lazy(() => import('pages/Trips/Trips'));
const DriversOnline = React.lazy(() => import('pages/DriversOnline/DriversOnline'));
const Passengers = React.lazy(() => import('pages/Passengers/Passengers'));
const DriverShifts = React.lazy(() => import('pages/DriverShifts/DriverShifts'));
const DriverShiftsTemplates = React.lazy(() => import('pages/DriverShiftsTemplates/DriverShiftsTemplates'));
const LiveMap = React.lazy(() => import('pages/LiveMap/LiveMap'));
const Logout = React.lazy(() => import('pages/LogoutContainer/LogoutContainer'));
const SystemSettings = React.lazy(() => import('pages/SystemSettings/SystemSettings'));
const Driver = React.lazy(() => import('pages/Drivers/Drivers'));
const Vehicles = React.lazy(() => import('pages/Vehicles/Vehicles'));
const Profiles = React.lazy(() => import('pages/Profiles/Profiles'));
const Users = React.lazy(() => import('pages/Users/Users'));
const Fares = React.lazy(() => import('pages/Fares/Fares'));
const Rates = React.lazy(() => import('pages/Rates/Rates'));
const Services = React.lazy(() => import('pages/Services/Services'));
const Holidays = React.lazy(() => import('pages/Holidays/Holidays'));
const Areas = React.lazy(() => import('pages/Areas/Areas'));
const Shuttles = React.lazy(() => import('pages/Shuttles/Shuttles'));
const Locations = React.lazy(() => import('pages/Locations/Locations'));
const CreateShuttle = React.lazy(() => import('pages/Shuttles/ShuttleForm/CreateShuttle/CreateShuttle'));
const ShuttleEdit = React.lazy(() => import('pages/Shuttles/ShuttleForm/UpdateShuttle/UpdateShuttle'));
const Suppliers = React.lazy(() => import('pages/Suppliers/Suppliers'));
const Shifts = React.lazy(() => import('pages/Shifts/Shifts'));
const ExtraServices = React.lazy(() => import('pages/ExtraServices/ExtraServices'));
const Reports = React.lazy(() => import('pages/Reports/Reports'));
const ShuttleReports = React.lazy(() => import('pages/Reports/ShuttleReports/ShuttleReports'));
const UniqueDriversReport = React.lazy(() => import('pages/Reports/UniqueDriversReport/UniqueDriversReport'));
const TripsPaxReports = React.lazy(() => import('pages/Reports/TripsPaxReports/TripsPaxReports'));
const DriverReports = React.lazy(() => import('pages/Reports/DriverReports/DriverReports'));
const VehicleReports = React.lazy(() => import('pages/Reports/VehicleReports/VehicleReports'));
const BillReports = React.lazy(() => import('pages/Reports/BillReports/BillReports'));
const Permissions = React.lazy(() => import('pages/Permissions/Permissions'));
const Sites = React.lazy(() => import('pages/Sites/Sites'));
const CreateScenario = React.lazy(() => import('pages/CreateScenario/CreateScenario'));
const Attributes = React.lazy(() => import('pages/Attributes/Attributes'));
const CreateJob = React.lazy(() => import('pages/CreateJob/CreateJob'));
const QuickJob = React.lazy(() => import('pages/QuickJob/QuickJob'));
const Layout = React.lazy(() => import('pages/Layout/Layout'));
const Onboarding = React.lazy(() => import('pages/Onboarding/Onboarding'));
const CardBlacklist = React.lazy(() => import('pages/CardBlacklist/CardBlacklist'));
const Integrations = React.lazy(() => import('pages/Integrations/Integrations'));
const Automations = React.lazy(() => import('pages/Automations/Automations'));
const Parents = React.lazy(() => import('pages/Parents/Parents'));
const CustomRules = React.lazy(() => import('pages/Rules/CustomRules'));
const PerformanceDashboard = React.lazy(() => import('pages/PerformanceDashboard/PerformanceDashboard'));
const LocationFacilityTypes = React.lazy(() => import('pages/LocationFacilityTypes/LocationFacilityTypes'));
const RoiSimulation = React.lazy(() => import('pages/RoiSimulation/RoiSimulation'));
const Feedbacks = React.lazy(() => import('pages/Feedbacks/Feedbacks'));
const ApiKeys = React.lazy(() => import('pages/ApiKeys/ApiKeys'));

// Auth routes
const Auth = React.lazy(() => import('pages/Auth/Auth'));
const ForgotPassword = React.lazy(() => import('pages/ForgotPassword/ForgotPassword'));
const ResetPassword = React.lazy(() => import('pages/ResetPassword/ResetPassword'));
const Signup = React.lazy(() => import('pages/Signup/Signup'));

type BaseRoute = {
  path: string;
  name?: string;
  module?: { code: string; action: string; fallback?: string } | string;
  guard?: ((guardData: GuardData) => boolean)[];
  /** This flag ensures that the component loads only after all the parameters necessary for its proper operation have been loaded. */
  shouldWaitForAppParameters?: boolean;
  fallback?: string;
  children?: Route[] | null;
};

/**
 * The pages the app lands on when the home page is not available, by order of preference.
 * The first one the user is allowed to view wins, and when none of them is allowed we land on
 * '/profile', which carries no permission guard.
 *
 * Landing on a page the user cannot view makes the app bounce between that page and the catch-all
 * route forever, which freezes the whole UI, so the target must always be a page the user can open.
 */
const landingPages: { path: string; module: ModuleCodes }[] = [
  /** Jobs, trips and reports come first, they are where users landed before the rest of the list existed */
  { path: '/bookings', module: ModuleCodes.Reservations },
  { path: '/trips', module: ModuleCodes.Trips },
  { path: '/reports', module: ModuleCodes.Reports },
  { path: '/timeline', module: ModuleCodes.Timeline },
  { path: '/passengers', module: ModuleCodes.Passengers },
  { path: '/drivers', module: ModuleCodes.Drivers },
  { path: '/onlinedrivers', module: ModuleCodes.OnlineDrivers },
  { path: '/driver-shifts', module: ModuleCodes.DriverShifts },
  { path: '/driver-shifts-templates', module: ModuleCodes.DriverShiftsTemplates },
  { path: '/livemap', module: ModuleCodes.LiveMap },
  { path: '/vehicles', module: ModuleCodes.Vehicles },
  { path: '/feedbacks', module: ModuleCodes.Feedbacks },
  { path: '/profiles', module: ModuleCodes.Profiles },
];

export const useAuthenticatedRoutes = (): Route[] => {
  const perm = usePerm();
  const { t } = useOperationTranslation();

  const isCompanyAdmin = !!useSelector((app: AppState) => app.auth.session?.isCompanyAdmin);
  const hideRoiSimulation = useSelector((app: AppState) => app.ui.hideRoiSimulation);

  const canViewHome = perm.can(ModuleActions.MissionControl.View, ModuleCodes.MissionControl);

  const fallback = useMemo(() => {
    /** 'View' is the action the pages above are guarded with, see accessGuard */
    const landingPage = landingPages.find(({ module }) => perm.can('View', module));

    return landingPage?.path || '/profile';
  }, [perm]);

  /** The home page redirects on its own when it is not available, sending unknown paths there would loop */
  const unknownPath = canViewHome ? '/' : fallback;

  const routes: Route[] = useMemo(() => {
    return [
      { path: '*', name: t('routes.home'), jsx: <Navigate to={unknownPath} replace></Navigate> },
      {
        path: '',
        name: t('routes.home'),
        component: Home,
        guard: [
          guards.accessGuard(ModuleCodes.MissionControl),
          guards.accessGuard({ code: ModuleCodes.MissionControl, action: ModuleActions.MissionControl.View }),
        ],
        fallback,
      },
      { path: 'agent', name: t('routes.home'), component: HomeAgent },
      { path: 'timeline', name: t('routes.timeline'), component: Timeline, guard: [guards.accessGuard(ModuleCodes.Timeline)] },
      { path: 'onboarding', name: t('routes.onboarding'), component: Onboarding, guard: [guards.accessGuard(ModuleCodes.Timeline)] },
      { path: 'sites/scenario', name: t('routes.newScenario'), component: CreateScenario },

      {
        path: 'bookings',
        name: t('routes.reservations'),
        shouldWaitForAppParameters: true,
        jsx: <Jobs viewType={JobsViewType.Table}></Jobs>,
        guard: [guards.accessGuard(ModuleCodes.Reservations)],
      },
      {
        path: 'bookings/create/normal',
        name: t('routes.reservations'),
        shouldWaitForAppParameters: true,
        jsx: <Jobs viewType={JobsViewType.Table} type="create"></Jobs>,
        guard: [
          guards.accessGuard(ModuleCodes.Reservations),
          guards.accessGuard({ code: ModuleCodes.Reservations, action: ModuleActions.Jobs.Create }),
        ],
        fallback: '/bookings',
      },
      {
        path: 'bookings/create/default',
        name: t('routes.reservations'),
        shouldWaitForAppParameters: true,
        jsx: <Jobs viewType={JobsViewType.Table} type="default"></Jobs>,
        guard: [guards.accessGuard(ModuleCodes.Reservations)],
        fallback: '/bookings',
      },
      {
        path: 'bookings/create/bulk',
        name: t('routes.reservations'),
        shouldWaitForAppParameters: true,
        jsx: <Jobs viewType={JobsViewType.Table} type="bulk"></Jobs>,
        guard: [
          guards.accessGuard(ModuleCodes.Reservations),
          guards.accessGuard({ code: ModuleCodes.Reservations, action: ModuleActions.Jobs.Create }),
        ],
        fallback: '/bookings',
      },

      {
        path: 'bookings/create/shuttle',
        name: t('routes.reservations'),
        shouldWaitForAppParameters: true,
        jsx: <Jobs viewType={JobsViewType.Table} type="shuttle"></Jobs>,
        guard: [
          guards.accessGuard(ModuleCodes.Reservations),
          guards.accessGuard({ code: ModuleCodes.Reservations, action: ModuleActions.Jobs.Create }),
        ],
        fallback: '/bookings',
      },
      {
        path: 'bookings/import',
        name: t('routes.reservations'),
        shouldWaitForAppParameters: true,
        jsx: <Jobs viewType={JobsViewType.Table} type="import"></Jobs>,
        guard: [
          guards.accessGuard(ModuleCodes.Reservations),
          guards.accessGuard({ code: ModuleCodes.DataUpload, action: ModuleActions.DataUpload.Import }),
        ],
        fallback: '/bookings',
      },
      {
        path: 'bookings/edit/:id',
        name: t('routes.reservations'),
        shouldWaitForAppParameters: true,
        jsx: <Jobs viewType={JobsViewType.Table} type="edit"></Jobs>,
        guard: [
          guards.accessGuard(ModuleCodes.Reservations),
          guards.accessGuard({ code: ModuleCodes.Reservations, action: ModuleActions.Jobs.View }),
        ],
        fallback: '/bookings',
      },
      {
        path: 'bookings/calendar',
        name: t('routes.jobsCalendar'),
        shouldWaitForAppParameters: true,
        jsx: <Jobs viewType={JobsViewType.Calendar}></Jobs>,
        guard: [guards.accessGuard(ModuleCodes.Reservations)],
      },
      {
        path: 'bookings/calendar/create/normal',
        name: t('routes.jobsCalendar'),
        shouldWaitForAppParameters: true,
        jsx: <Jobs viewType={JobsViewType.Calendar} type="create"></Jobs>,
        guard: [
          guards.accessGuard(ModuleCodes.Reservations),
          guards.accessGuard({ code: ModuleCodes.Reservations, action: ModuleActions.Jobs.Create }),
        ],
        fallback: '/bookings',
      },
      {
        path: 'bookings/calendar/create/default',
        name: t('routes.jobsCalendar'),
        shouldWaitForAppParameters: true,
        jsx: <Jobs viewType={JobsViewType.Calendar} type="default"></Jobs>,
        guard: [guards.accessGuard(ModuleCodes.Reservations)],
        fallback: '/bookings',
      },
      {
        path: 'bookings/calendar/create/bulk',
        name: t('routes.jobsCalendar'),
        shouldWaitForAppParameters: true,
        jsx: <Jobs viewType={JobsViewType.Calendar} type="bulk"></Jobs>,
        guard: [
          guards.accessGuard(ModuleCodes.Reservations),
          guards.accessGuard({ code: ModuleCodes.Reservations, action: ModuleActions.Jobs.Create }),
        ],
        fallback: '/bookings',
      },

      {
        path: 'bookings/calendar/create/shuttle',
        name: t('routes.jobsCalendar'),
        shouldWaitForAppParameters: true,
        jsx: <Jobs viewType={JobsViewType.Calendar} type="shuttle"></Jobs>,
        guard: [
          guards.accessGuard(ModuleCodes.Reservations),
          guards.accessGuard({ code: ModuleCodes.Reservations, action: ModuleActions.Jobs.Create }),
        ],
        fallback: '/bookings',
      },
      {
        path: 'bookings/calendar/import',
        name: t('routes.jobsCalendar'),
        shouldWaitForAppParameters: true,
        jsx: <Jobs viewType={JobsViewType.Calendar} type="import"></Jobs>,
        guard: [
          guards.accessGuard(ModuleCodes.Reservations),
          guards.accessGuard({ code: ModuleCodes.DataUpload, action: ModuleActions.DataUpload.Import }),
        ],
        fallback: '/bookings',
      },
      {
        path: 'bookings/calendar/edit/:id',
        name: t('routes.jobsCalendar'),
        shouldWaitForAppParameters: true,
        jsx: <Jobs viewType={JobsViewType.Calendar} type="edit"></Jobs>,
        guard: [guards.accessGuard(ModuleCodes.Reservations)],
        fallback: '/bookings/calendar',
      },

      {
        path: 'trips',
        name: t('routes.trips'),
        shouldWaitForAppParameters: true,
        component: Trips,
        guard: [guards.accessGuard(ModuleCodes.Trips)],
      },
      {
        path: 'trips/:productionId',
        name: t('routes.trips'),
        shouldWaitForAppParameters: true,
        component: Trips,
        guard: [guards.accessGuard(ModuleCodes.Trips)],
      },
      {
        path: 'trips/view/:id',
        name: t('routes.trips'),
        shouldWaitForAppParameters: true,
        component: Trips,
        guard: [guards.accessGuard(ModuleCodes.Trips)],
      },

      { path: 'drivers', name: t('routes.drivers'), component: Driver, guard: [guards.accessGuard(ModuleCodes.Drivers)] },
      // { path: 'drivers/*',                           name: t('routes.drivers'),                        jsx: <Navigate to="/drivers" replace></Navigate>,                 guard: [guards.accessGuard(ModuleCodes.Drivers)]                                                                                                                                             },
      {
        path: 'drivers/create',
        name: t('routes.drivers'),
        jsx: <Driver type="create"></Driver>,
        guard: [
          guards.accessGuard(ModuleCodes.Drivers),
          guards.accessGuard({ code: ModuleCodes.Drivers, action: ModuleActions.Drivers.Create }),
        ],
        fallback: '/drivers',
      },
      {
        path: 'drivers/import',
        name: t('routes.drivers'),
        jsx: <Driver type="import"></Driver>,
        guard: [
          guards.accessGuard(ModuleCodes.Drivers),
          guards.accessGuard({ code: ModuleCodes.DataUpload, action: ModuleActions.DataUpload.Import }),
        ],
        fallback: '/drivers',
      },
      {
        path: 'drivers/edit/:id',
        name: t('routes.drivers'),
        jsx: <Driver type="edit"></Driver>,
        guard: [
          guards.accessGuard(ModuleCodes.Drivers),
          guards.accessGuard({ code: ModuleCodes.Drivers, action: ModuleActions.Drivers.View }),
        ],
        fallback: '/drivers',
      },

      {
        path: 'onlinedrivers',
        name: t('routes.onlineDrivers'),
        component: DriversOnline,
        guard: [guards.accessGuard(ModuleCodes.OnlineDrivers)],
      },
      // { path: 'onlinedrivers/*',                     name: t('routes.onlineDrivers'),                  jsx: <Navigate to="/onlineDrivers" replace></Navigate>,           guard: [guards.accessGuard(ModuleCodes.ActiveDrivers)]                                                                                                                                             },
      {
        path: 'onlinedrivers/edit/:id',
        name: t('routes.onlineDrivers'),
        jsx: <DriversOnline type="edit"></DriversOnline>,
        guard: [
          guards.accessGuard(ModuleCodes.Drivers),
          guards.accessGuard({ code: ModuleCodes.Drivers, action: ModuleActions.Drivers.Edit }),
        ],
        fallback: '/drivers',
      },

      // { path: 'passengers/*',                    name: t('routes.passengers'),                     jsx: <Navigate to="/passengers" replace></Navigate>,                    guard: [guards.accessGuard(ModuleCodes.Passengers)]                                                                                                                                                                                                                                                                                         },
      {
        path: 'passengers',
        name: t('routes.passengers'),
        jsx: <Passengers passengerType={PassengerTypes.Default}></Passengers>,
        guard: [guards.accessGuard(ModuleCodes.Passengers)],
      },
      {
        path: 'students',
        name: t('routes.passengers'),
        jsx: <Passengers passengerType={PassengerTypes.Student}></Passengers>,
        guard: [guards.accessGuard(ModuleCodes.Passengers)],
      },
      {
        path: 'employees',
        name: t('routes.passengers'),
        jsx: <Passengers passengerType={PassengerTypes.Employee}></Passengers>,
        guard: [guards.accessGuard(ModuleCodes.Passengers)],
      },
      {
        path: 'caregivers',
        name: t('routes.caregivers'),
        jsx: <Passengers passengerType={PassengerTypes.Caregiver}></Passengers>,
        guard: [guards.accessGuard(ModuleCodes.Caregivers)],
      },
      {
        path: 'passengers/edit/:id',
        name: t('routes.passengers'),
        jsx: <Passengers type="edit" passengerType={PassengerTypes.Default}></Passengers>,
        guard: [guards.accessGuard({ code: ModuleCodes.Passengers, action: ModuleActions.Passengers.View })],
        fallback: '/passengers',
      },
      {
        path: 'students/edit/:id',
        name: t('routes.students'),
        jsx: <Passengers type="edit" passengerType={PassengerTypes.Student}></Passengers>,
        guard: [guards.accessGuard({ code: ModuleCodes.Passengers, action: ModuleActions.Passengers.Edit })],
        fallback: '/students',
      },
      {
        path: 'employees/edit/:id',
        name: t('routes.passengers'),
        jsx: <Passengers type="edit" passengerType={PassengerTypes.Employee}></Passengers>,
        guard: [guards.accessGuard({ code: ModuleCodes.Passengers, action: ModuleActions.Passengers.View })],
        fallback: '/passengers',
      },
      {
        path: 'caregivers/edit/:id',
        name: t('routes.caregivers'),
        jsx: <Passengers type="edit" passengerType={PassengerTypes.Caregiver}></Passengers>,
        guard: [guards.accessGuard({ code: ModuleCodes.Caregivers, action: ModuleActions.Caregivers.View })],
        fallback: '/caregivers',
      },
      {
        path: 'passengers/create',
        name: t('routes.passengers'),
        jsx: <Passengers type="create" passengerType={PassengerTypes.Default}></Passengers>,
        guard: [
          guards.accessGuard(ModuleCodes.Passengers),
          guards.accessGuard({ code: ModuleCodes.Passengers, action: ModuleActions.Passengers.Create }),
          guards.operationGuard([OperationTypes.Default, OperationTypes.Broker]),
        ],
        fallback: '/passengers',
      },
      {
        path: 'passengers/import',
        name: t('routes.passengers'),

        jsx: <Passengers type="import" passengerType={PassengerTypes.Default}></Passengers>,
        guard: [
          guards.accessGuard(ModuleCodes.Passengers),
          guards.accessGuard({ code: ModuleCodes.DataUpload, action: ModuleActions.DataUpload.Import }),
        ],
        fallback: '/passengers',
      },
      {
        path: 'students/create',
        name: t('routes.students'),
        jsx: <Passengers type="create" passengerType={PassengerTypes.Student}></Passengers>,
        guard: [
          guards.accessGuard(ModuleCodes.Passengers),
          guards.accessGuard({ code: ModuleCodes.Passengers, action: ModuleActions.Passengers.Create }),
          guards.operationGuard([OperationTypes.Default, OperationTypes.School]),
        ],
        fallback: '/students',
      },
      {
        path: 'students/import',
        name: t('routes.passengers'),
        jsx: <Passengers type="import" passengerType={PassengerTypes.Student}></Passengers>,
        guard: [
          guards.accessGuard(ModuleCodes.Passengers),
          guards.accessGuard({ code: ModuleCodes.DataUpload, action: ModuleActions.DataUpload.Import }),
        ],
        fallback: '/students',
      },
      {
        path: 'employees/create',
        name: t('routes.employees'),
        jsx: <Passengers type="create" passengerType={PassengerTypes.Employee}></Passengers>,
        guard: [
          guards.accessGuard(ModuleCodes.Passengers),
          guards.accessGuard({ code: ModuleCodes.Passengers, action: ModuleActions.Passengers.Create }),
          guards.operationGuard([OperationTypes.Default, OperationTypes.Employee]),
        ],
        fallback: '/employees',
      },
      {
        path: 'employees/import',
        name: t('routes.passengers'),
        jsx: <Passengers type="import" passengerType={PassengerTypes.Employee}></Passengers>,
        guard: [
          guards.accessGuard(ModuleCodes.Passengers),
          guards.accessGuard({ code: ModuleCodes.DataUpload, action: ModuleActions.DataUpload.Import }),
        ],
        fallback: '/employees',
      },
      {
        path: 'caregivers/create',
        name: t('routes.caregivers'),
        jsx: <Passengers type="create" passengerType={PassengerTypes.Caregiver}></Passengers>,
        guard: [
          guards.accessGuard(ModuleCodes.Caregivers),
          guards.accessGuard({ code: ModuleCodes.Caregivers, action: ModuleActions.Caregivers.Create }),
          guards.operationGuard([OperationTypes.Default, OperationTypes.School]),
        ],
        fallback: '/caregivers',
      },

      { path: 'vehicles', name: t('routes.vehicles'), component: Vehicles, guard: [guards.accessGuard(ModuleCodes.Vehicles)] },
      // { path: 'vehicles/*',                          name: t('routes.vehicles'),                       jsx: <Navigate to="/vehicles" replace></Navigate>,                guard: [guards.accessGuard(ModuleCodes.Vehicles)]                                                                                                                                             },
      {
        path: 'vehicles/create',
        name: t('routes.vehicles'),
        jsx: <Vehicles type="create"></Vehicles>,
        guard: [
          guards.accessGuard(ModuleCodes.Vehicles),
          guards.accessGuard({ code: ModuleCodes.Vehicles, action: ModuleActions.Vehicles.Create }),
        ],
        fallback: '/vehicles',
      },
      {
        path: 'vehicles/import',
        name: t('routes.vehicles'),
        jsx: <Vehicles type="import"></Vehicles>,
        guard: [
          guards.accessGuard(ModuleCodes.Vehicles),
          guards.accessGuard({ code: ModuleCodes.DataUpload, action: ModuleActions.DataUpload.Import }),
        ],
        fallback: '/vehicles',
      },
      {
        path: 'vehicles/edit/:id',
        name: t('routes.vehicles'),
        jsx: <Vehicles type="edit"></Vehicles>,
        guard: [
          guards.accessGuard(ModuleCodes.Vehicles),
          guards.accessGuard({ code: ModuleCodes.Vehicles, action: ModuleActions.Vehicles.View }),
        ],
        fallback: '/vehicles',
      },

      { path: 'livemap', name: t('routes.liveMap'), component: LiveMap, guard: [guards.accessGuard(ModuleCodes.LiveMap)] },

      {
        path: 'driver-shifts',
        name: t('routes.driverShifts'),
        component: DriverShifts,
        guard: [guards.accessGuard(ModuleCodes.DriverShifts)],
      },
      {
        path: 'driver-shifts/create',
        name: t('routes.driverShifts'),
        jsx: <DriverShifts type="create"></DriverShifts>,
        guard: [
          guards.accessGuard(ModuleCodes.DriverShifts),
          guards.accessGuard({ code: ModuleCodes.DriverShifts, action: ModuleActions.DriverShifts.Create }),
        ],
        fallback: '/driver-shifts',
      },
      {
        path: 'driver-shifts/create/:driverId/:start/:end',
        name: t('routes.driverShifts'),
        jsx: <DriverShifts type="create"></DriverShifts>,
        guard: [
          guards.accessGuard(ModuleCodes.DriverShifts),
          guards.accessGuard({ code: ModuleCodes.DriverShifts, action: ModuleActions.DriverShifts.Create }),
        ],
        fallback: '/driver-shifts',
      },
      {
        path: 'driver-shifts/import',
        name: t('routes.driverShifts'),
        jsx: <DriverShifts type="import"></DriverShifts>,
        guard: [
          guards.accessGuard(ModuleCodes.DriverShifts),
          guards.accessGuard({ code: ModuleCodes.DataUpload, action: ModuleActions.DataUpload.Import }),
        ],
        fallback: '/driver-shifts',
      },
      {
        path: 'driver-shifts/edit/:id',
        name: t('routes.driverShifts'),
        jsx: <DriverShifts type="edit"></DriverShifts>,
        guard: [
          guards.accessGuard(ModuleCodes.DriverShifts),
          guards.accessGuard({ code: ModuleCodes.DriverShifts, action: ModuleActions.DriverShifts.View }),
        ],
        fallback: '/driver-shifts',
      },

      {
        path: 'driver-shifts-templates',
        name: t('routes.driverShiftsTemplates'),
        component: DriverShiftsTemplates,
        guard: [guards.accessGuard(ModuleCodes.DriverShiftsTemplates)],
      },
      {
        path: 'driver-shifts-templates/create',
        name: t('routes.driverShiftsTemplates'),
        jsx: <DriverShiftsTemplates type="create"></DriverShiftsTemplates>,
        guard: [
          guards.accessGuard(ModuleCodes.DriverShiftsTemplates),
          guards.accessGuard({ code: ModuleCodes.DriverShiftsTemplates, action: ModuleActions.DriverShiftsTemplates.Create }),
        ],
        fallback: '/driver-shifts-templates',
      },
      {
        path: 'driver-shifts-templates/edit/:id',
        name: t('routes.driverShiftsTemplates'),
        jsx: <DriverShiftsTemplates type="edit"></DriverShiftsTemplates>,
        guard: [
          guards.accessGuard(ModuleCodes.DriverShiftsTemplates),
          guards.accessGuard({ code: ModuleCodes.DriverShiftsTemplates, action: ModuleActions.DriverShiftsTemplates.View }),
        ],
        fallback: '/driver-shifts-templates',
      },

      { path: 'reports', name: t('routes.reports'), component: Reports, guard: [guards.accessGuard(ModuleCodes.Reports)] },
      // { path: 'reports/*',                           name: t('routes.reports'),                        jsx: <Navigate to="/reports" replace></Navigate>,                 guard: [guards.accessGuard(ModuleCodes.Reports)]                                                                                                                                             },
      {
        path: 'reports/trips-pax-reports',
        name: t('routes.tripsPaxReports'),
        component: TripsPaxReports,
        guard: [guards.accessGuard(ModuleCodes.Reports)],
      },
      {
        path: 'reports/shuttle-reports',
        name: t('routes.shuttleReports'),
        component: ShuttleReports,
        guard: [guards.accessGuard(ModuleCodes.Reports)],
      },
      {
        path: 'reports/unique-drivers-report',
        name: t('routes.uniqueDriversReport'),
        component: UniqueDriversReport,
        guard: [guards.accessGuard(ModuleCodes.Reports)],
      },
      {
        path: 'reports/drivers-reports',
        name: t('driverReport.driverReportTitle'),
        component: DriverReports,
        guard: [guards.accessGuard(ModuleCodes.Reports)],
      },
      {
        path: 'reports/vehicles-reports',
        name: t('vehicleReport.vehicleReportTitle'),
        component: VehicleReports,
        guard: [guards.accessGuard(ModuleCodes.Reports)],
      },

      {
        path: 'reports/bill-reports',
        name: t('billReports.vehicleReportTitle'),
        component: BillReports,
        guard: [guards.accessGuard(ModuleCodes.Reports)],
      },

      {
        path: 'integrations',
        name: t('routes.integrations'),
        component: Integrations,
        guard: [guards.accessGuard(ModuleCodes.Integrations)],
      },
      {
        path: 'integrations/:service',
        name: t('routes.integrations'),
        component: Integrations,
        guard: [guards.accessGuard(ModuleCodes.Integrations)],
      },

      {
        path: 'reports/performance',
        name: t('routes.performanceOverview'),
        component: PerformanceDashboard,
        guard: [guards.accessGuard(ModuleCodes.Reports)],
      },

      { path: 'services', name: t('routes.services'), component: Services, guard: [guards.accessGuard(ModuleCodes.Services)] },
      // { path: 'services/*',                          name: t('routes.services'),                       jsx: <Navigate to="/services" replace></Navigate>,                guard: [guards.accessGuard(ModuleCodes.Services)]                                                                                                                                             },
      {
        path: 'services/create',
        name: t('routes.services'),
        jsx: <Services type="create"></Services>,
        guard: [
          guards.accessGuard(ModuleCodes.Services),
          guards.accessGuard({ code: ModuleCodes.Services, action: ModuleActions.Services.Create }),
        ],
        fallback: '/services',
      },
      {
        path: 'services/edit/:id',
        name: t('routes.services'),
        jsx: <Services type="edit"></Services>,
        guard: [
          guards.accessGuard(ModuleCodes.Services),
          guards.accessGuard({ code: ModuleCodes.Services, action: ModuleActions.Services.Edit }),
        ],
        fallback: '/services',
      },

      { path: 'locations', name: t('routes.locations'), component: Locations, guard: [guards.accessGuard(ModuleCodes.Locations)] },
      // { path: 'locations/*',                         name: t('routes.locations'),                      jsx: <Navigate to="/locations" replace></Navigate>,               guard: [guards.accessGuard(ModuleCodes.Locations)]                                                                                                                                             },
      {
        path: 'locations/create',
        name: t('routes.locations'),
        jsx: <Locations type="create"></Locations>,
        guard: [
          guards.accessGuard(ModuleCodes.Locations),
          guards.accessGuard({ code: ModuleCodes.Locations, action: ModuleActions.Locations.Create }),
        ],
        fallback: '/locations',
      },
      {
        path: 'locations/import',
        name: t('routes.locations'),
        jsx: <Locations type="import"></Locations>,
        guard: [
          guards.accessGuard(ModuleCodes.Locations),
          guards.accessGuard({ code: ModuleCodes.DataUpload, action: ModuleActions.DataUpload.Import }),
        ],
        fallback: '/locations',
      },
      {
        path: 'locations/edit/:id',
        name: t('routes.locations'),
        jsx: <Locations type="edit"></Locations>,
        guard: [
          guards.accessGuard(ModuleCodes.Locations),
          guards.accessGuard({ code: ModuleCodes.Locations, action: ModuleActions.Locations.View }),
        ],
        fallback: '/locations',
      },

      { path: 'fares', name: t('routes.fares'), component: Fares, guard: [guards.accessGuard(ModuleCodes.Fares)] },
      // { path: 'fares/*',                             name: t('routes.fares'),                          jsx: <Navigate to="/fares" replace></Navigate>,                   guard: [guards.accessGuard(ModuleCodes.Fares)]                                                                                                                                             },
      {
        path: 'fares/create',
        name: t('routes.fares'),
        jsx: <Fares type="create"></Fares>,
        guard: [guards.accessGuard(ModuleCodes.Fares), guards.accessGuard({ code: ModuleCodes.Fares, action: ModuleActions.Fares.Create })],
        fallback: '/fares',
      },
      {
        path: 'fares/edit/:id',
        name: t('routes.fares'),
        jsx: <Fares type="edit"></Fares>,
        guard: [guards.accessGuard(ModuleCodes.Fares), guards.accessGuard({ code: ModuleCodes.Fares, action: ModuleActions.Fares.Edit })],
        fallback: '/fares',
      },

      { path: 'rates', name: t('routes.rates'), component: Rates, guard: [guards.accessGuard(ModuleCodes.Rates)] },
      // { path: 'rates/*',                             name: t('routes.rates'),                          jsx: <Navigate to="/rates" replace></Navigate>,                   guard: [guards.accessGuard(ModuleCodes.Rates)]                                                                                                                                             },
      {
        path: 'rates/create',
        name: t('routes.rates'),
        jsx: <Rates type="create"></Rates>,
        guard: [guards.accessGuard(ModuleCodes.Rates), guards.accessGuard({ code: ModuleCodes.Rates, action: ModuleActions.Rates.Create })],
        fallback: '/rates',
      },
      {
        path: 'rates/edit/:id',
        name: t('routes.rates'),
        jsx: <Rates type="edit"></Rates>,
        guard: [guards.accessGuard(ModuleCodes.Rates), guards.accessGuard({ code: ModuleCodes.Rates, action: ModuleActions.Rates.Edit })],
        fallback: '/rates',
      },

      {
        path: 'extraservices',
        name: t('routes.extraServices'),
        component: ExtraServices,
        guard: [guards.accessGuard(ModuleCodes.ExtraServices)],
      },
      // { path: 'extraservices/*',                     name: t('routes.extraservices'),                  jsx: <Navigate to="/extraservices" replace></Navigate>,           guard: [guards.accessGuard(ModuleCodes.ExtraServices)]                                                                                                                                             },
      {
        path: 'extraservices/create',
        name: t('routes.extraServices'),
        jsx: <ExtraServices type="create"></ExtraServices>,
        guard: [
          guards.accessGuard(ModuleCodes.ExtraServices),
          guards.accessGuard({ code: ModuleCodes.ExtraServices, action: ModuleActions.ExtraServices.Create }),
        ],
        fallback: '/extraServices',
      },
      {
        path: 'extraservices/edit/:id',
        name: t('routes.extraServices'),
        jsx: <ExtraServices type="edit"></ExtraServices>,
        guard: [
          guards.accessGuard(ModuleCodes.ExtraServices),
          guards.accessGuard({ code: ModuleCodes.ExtraServices, action: ModuleActions.ExtraServices.Edit }),
        ],
        fallback: '/extraservices',
      },

      { path: 'suppliers', name: t('routes.suppliers'), component: Suppliers, guard: [guards.accessGuard(ModuleCodes.Suppliers)] },
      // { path: 'suppliers/*',                         name: t('routes.suppliers'),                      jsx: <Navigate to="/suppliers" replace></Navigate>,               guard: [guards.accessGuard(ModuleCodes.Suppliers)]                                                                                                                                        },
      {
        path: 'suppliers/create',
        name: t('routes.suppliers'),
        jsx: <Suppliers type="create"></Suppliers>,
        guard: [
          guards.accessGuard(ModuleCodes.Suppliers),
          guards.accessGuard({ code: ModuleCodes.Suppliers, action: ModuleActions.Suppliers.Create }),
        ],
        fallback: '/suppliers',
      },
      {
        path: 'suppliers/import',
        name: t('routes.suppliers'),
        jsx: <Suppliers type="import"></Suppliers>,
        guard: [
          guards.accessGuard(ModuleCodes.Suppliers),
          guards.accessGuard({ code: ModuleCodes.DataUpload, action: ModuleActions.DataUpload.Import }),
        ],
        fallback: '/suppliers',
      },
      {
        path: 'suppliers/edit/:id',
        name: t('routes.suppliers'),
        jsx: <Suppliers type="edit"></Suppliers>,
        guard: [
          guards.accessGuard(ModuleCodes.Suppliers),
          guards.accessGuard({ code: ModuleCodes.Suppliers, action: ModuleActions.Suppliers.View }),
        ],
        fallback: '/suppliers',
      },

      { path: 'profile', name: t('routes.profile'), component: UserProfile },
      { path: 'users', name: t('routes.users'), component: Users, guard: [guards.accessGuard(ModuleCodes.Users)] },
      // { path: 'users/*',                             name: t('routes.users'),                          jsx: <Navigate to="/users" replace></Navigate>,                   guard: [guards.accessGuard(ModuleCodes.Users)]                                                                                                                                            },
      {
        path: 'users/edit/:id',
        name: t('routes.users'),
        jsx: <Users type="edit"></Users>,
        guard: [guards.accessGuard(ModuleCodes.Users), guards.accessGuard({ code: ModuleCodes.Users, action: ModuleActions.Users.View })],
        fallback: '/users',
      },

      { path: 'profiles', name: t('routes.profiles'), component: Profiles, guard: [guards.accessGuard(ModuleCodes.Profiles)] },
      // { path: 'profiles/*',                          name: t('routes.profiles'),                       jsx: <Navigate to="/profiles" replace></Navigate>,                guard: [guards.accessGuard(ModuleCodes.Profiles)]                                                                                                                                         },
      {
        path: 'profiles/create',
        name: t('routes.profiles'),
        jsx: <Profiles type="create"></Profiles>,
        guard: [
          guards.accessGuard(ModuleCodes.Profiles),
          guards.accessGuard({ code: ModuleCodes.Profiles, action: ModuleActions.Profiles.Create }),
        ],
        fallback: '/profiles',
      },
      {
        path: 'profiles/edit/:id',
        name: t('routes.profiles'),
        jsx: <Profiles type="edit"></Profiles>,
        guard: [
          guards.accessGuard(ModuleCodes.Profiles),
          guards.accessGuard({ code: ModuleCodes.Profiles, action: ModuleActions.Profiles.View }),
        ],
        fallback: '/profiles',
      },

      { path: 'shifts', name: t('routes.shifts'), component: Shifts, guard: [guards.accessGuard(ModuleCodes.Shifts)] },
      // { path: 'shifts/*',                            name: t('routes.shifts'),                         jsx: <Navigate to="/shifts" replace></Navigate>,              guard: [guards.accessGuard(ModuleCodes.Shifts)]                                                                                                                                           },
      {
        path: 'shifts/create',
        name: t('routes.shifts'),
        jsx: <Shifts type="create"></Shifts>,
        guard: [
          guards.accessGuard(ModuleCodes.Shifts),
          guards.accessGuard({ code: ModuleCodes.Shifts, action: ModuleActions.Shifts.Create }),
        ],
        fallback: '/shifts',
      },
      {
        path: 'shifts/edit/:id',
        name: t('routes.shifts'),
        jsx: <Shifts type="edit"></Shifts>,
        guard: [
          guards.accessGuard(ModuleCodes.Shifts),
          guards.accessGuard({ code: ModuleCodes.Shifts, action: ModuleActions.Shifts.Edit }),
        ],
        fallback: '/shifts',
      },

      { path: 'shuttles', name: t('routes.shuttles'), component: Shuttles, guard: [guards.accessGuard(ModuleCodes.Shuttles)] },
      // { path: 'shuttles/*',                          name: t('routes.shuttles'),                       jsx: <Navigate to="/shuttles" replace></Navigate>,                guard: [guards.accessGuard(ModuleCodes.Shuttles)]                                                                                                                                         },
      {
        path: 'shuttles/create',
        name: t('routes.createShuttle'),
        component: CreateShuttle,
        guard: [
          guards.accessGuard(ModuleCodes.Shuttles),
          guards.accessGuard({ code: ModuleCodes.Shuttles, action: ModuleActions.Shuttles.Create }),
        ],
        fallback: '/shuttles',
      },
      {
        path: 'shuttles/edit/:id',
        name: t('routes.editShuttle'),
        component: ShuttleEdit,
        guard: [
          guards.accessGuard(ModuleCodes.Shuttles),
          guards.accessGuard({ code: ModuleCodes.Shuttles, action: ModuleActions.Shuttles.Edit }),
        ],
        fallback: '/shuttles',
      },

      {
        path: 'create-job',
        name: t('routes.createJob'),
        component: CreateJob,
        guard: [
          guards.accessGuard(ModuleCodes.Reservations),
          guards.accessGuard({ code: ModuleCodes.Reservations, action: ModuleActions.Jobs.Create }),
        ],
        fallback: '/bookings',
      },
      {
        path: 'quick-job',
        name: t('routes.quickJob'),
        component: QuickJob,
        guard: [
          guards.accessGuard(ModuleCodes.Reservations),
          guards.accessGuard({ code: ModuleCodes.Reservations, action: ModuleActions.Jobs.Create }),
        ],
        fallback: '/bookings',
      },
      { path: 'logout', name: t('routes.logout'), component: Logout },
      { path: 'permissions', name: t('routes.permissions'), component: Permissions, guard: [() => isCompanyAdmin] },

      { path: 'settings', name: t('routes.settings'), component: SystemSettings, guard: [guards.accessGuard(ModuleCodes.Settings)] },
      // { path: 'settings/*',                          name: t('routes.settings'),                       jsx: <Navigate to="/settings" replace></Navigate>,                guard: [guards.accessGuard(ModuleCodes.Settings)]                                                                                                                                         },

      { path: 'api-keys', name: t('routes.apiKeys'), component: ApiKeys, guard: [guards.accessGuard(ModuleCodes.ApiKeys)] },
      {
        path: 'api-keys/create',
        name: t('routes.apiKeys'),
        jsx: <ApiKeys type="create"></ApiKeys>,
        guard: [
          guards.accessGuard(ModuleCodes.ApiKeys),
          guards.accessGuard({ code: ModuleCodes.ApiKeys, action: ModuleActions.ApiKeys.Create }),
        ],
        fallback: '/api-keys',
      },
      {
        path: 'api-keys/edit/:id',
        name: t('routes.apiKeys'),
        jsx: <ApiKeys type="edit"></ApiKeys>,
        guard: [
          guards.accessGuard(ModuleCodes.ApiKeys),
          guards.accessGuard({ code: ModuleCodes.ApiKeys, action: ModuleActions.ApiKeys.View }),
        ],
        fallback: '/api-keys',
      },

      { path: 'holidays', name: t('routes.holidays'), component: Holidays, guard: [guards.accessGuard(ModuleCodes.Holidays)] },
      // { path: 'holidays/*',                          name: t('routes.holidays'),                       jsx: <Navigate to="/holidays" replace></Navigate>,                                                                                                                                                       },
      {
        path: 'holidays/create',
        name: t('routes.holidays'),
        jsx: <Holidays type="create"></Holidays>,
        guard: [
          guards.accessGuard(ModuleCodes.Holidays),
          guards.accessGuard({ code: ModuleCodes.Holidays, action: ModuleActions.Holidays.Create }),
        ],
        fallback: 'areas',
      },
      {
        path: 'holidays/edit/:id',
        name: t('routes.holidays'),
        jsx: <Holidays type="edit"></Holidays>,
        guard: [
          guards.accessGuard(ModuleCodes.Holidays),
          guards.accessGuard({ code: ModuleCodes.Holidays, action: ModuleActions.Holidays.Edit }),
        ],
        fallback: 'areas',
      },

      { path: 'areas', name: t('routes.areas'), component: Areas, guard: [guards.accessGuard(ModuleCodes.Areas)] },
      // { path: 'areas/*',                             name: t('routes.areas'),                          jsx: <Navigate to="/areas" replace></Navigate>,                   guard: [guards.accessGuard(ModuleCodes.Areas)]                                                                                                                                                                                                                                 },
      {
        path: 'areas/create',
        name: t('routes.areas'),
        jsx: <Areas type="create"></Areas>,
        guard: [guards.accessGuard(ModuleCodes.Areas), guards.accessGuard({ code: ModuleCodes.Areas, action: ModuleActions.Areas.Create })],
        fallback: '/areas',
      },
      {
        path: 'areas/edit/:id',
        name: t('routes.areas'),
        jsx: <Areas type="edit"></Areas>,
        guard: [guards.accessGuard(ModuleCodes.Areas), guards.accessGuard({ code: ModuleCodes.Areas, action: ModuleActions.Areas.View })],
        fallback: '/areas',
      },

      { path: 'sites', name: t('routes.sites'), component: Sites, guard: [() => isCompanyAdmin] },
      // { path: 'sites/*',                             name: t('routes.sites'),                          jsx: <Navigate to="/sites" replace></Navigate>,                                                                                                                                                                                                                                    },
      { path: 'sites/create', name: t('routes.sites'), jsx: <Sites type="create"></Sites>, guard: [() => isCompanyAdmin] },
      { path: 'sites/edit/:id', name: t('routes.sites'), jsx: <Sites type="edit"></Sites>, guard: [() => isCompanyAdmin] },

      {
        path: 'attributes',
        name: t('routes.attributes'),
        component: Attributes /*guard: [guards.accessGuard(ModuleCodes.Attributes)] TODO: return in new server */,
      },
      // { path: 'attributes/*',                        name: t('routes.attributes'),                     jsx: <Navigate to="/attributes" replace></Navigate>,              /*guard: [guards.accessGuard(ModuleCodes.Attributes)] TODO: return*/                                                                                                                                                                                                                          },
      {
        path: 'attributes/create',
        name: t('routes.attributes'),
        jsx: (
          <Attributes type="create"></Attributes>
        ) /*guard: [guards.accessGuard(ModuleCodes.Attributes), guards.accessGuard({ code: ModuleCodes.Attributes, action: ModuleActions.Attributes.Create })], fallback: '/attributes' TODO: return*/,
      },
      {
        path: 'attributes/import',
        name: t('routes.attributes'),
        jsx: <Attributes type="import"></Attributes>,
        guard: [
          guards.accessGuard(ModuleCodes.Attributes),
          guards.accessGuard({ code: ModuleCodes.DataUpload, action: ModuleActions.DataUpload.Import }),
        ],
        fallback: '/attributes',
      },
      {
        path: 'attributes/edit/:id',
        name: t('routes.attributes'),
        jsx: (
          <Attributes type="edit"></Attributes>
        ) /*guard: [guards.accessGuard(ModuleCodes.Attributes), guards.accessGuard({ code: ModuleCodes.Attributes, action: ModuleActions.Attributes.Edit })], fallback: '/attributes'  TODO: return*/,
      },

      { path: 'blacklist', name: t('routes.blacklist'), component: CardBlacklist, guard: [guards.accessGuard(ModuleCodes.BlacklistCards)] },
      // { path: 'blacklist/*',                         name: t('routes.blacklist'),                       jsx: <Navigate to="/blacklist" replace></Navigate>,              /*guard: [guards.accessGuard(ModuleCodes.Attributes)] TODO: return*/                                                                                                                                                                                                                          },
      {
        path: 'blacklist/create',
        name: t('routes.blacklist'),
        jsx: <CardBlacklist type="create"></CardBlacklist>,
        guard: [
          guards.accessGuard(ModuleCodes.BlacklistCards),
          guards.accessGuard({ code: ModuleCodes.BlacklistCards, action: ModuleActions.Attributes.Create }),
        ],
        fallback: '/blacklist',
      },
      {
        path: 'blacklist/edit/:id',
        name: t('routes.blacklist'),
        jsx: <CardBlacklist type="edit"></CardBlacklist>,
        guard: [
          guards.accessGuard(ModuleCodes.BlacklistCards),
          guards.accessGuard({ code: ModuleCodes.BlacklistCards, action: ModuleActions.Attributes.Edit }),
        ],
        fallback: '/blacklist',
      },

      { path: 'automations', name: t('routes.automations'), component: Automations, guard: [guards.accessGuard(ModuleCodes.Automations)] },
      {
        path: 'automations/create',
        name: t('routes.automations'),
        jsx: <Automations type="create"></Automations>,
        guard: [guards.accessGuard(ModuleCodes.Automations)],
        fallback: '/automations',
      },
      {
        path: 'automations/edit/:id',
        name: t('routes.automations'),
        jsx: <Automations type="edit"></Automations>,
        guard: [guards.accessGuard(ModuleCodes.Automations)],
        fallback: '/automations',
      },
      // edit-template is equivalent to creation of a new automation
      {
        path: 'automations/edit-template/:id',
        name: t('routes.automations'),
        jsx: <Automations type="editTemplate"></Automations>,
        guard: [guards.accessGuard(ModuleCodes.Automations)],
        fallback: '/automations',
      },

      { path: 'automations', name: t('routes.automations'), component: Automations, guard: [guards.accessGuard(ModuleCodes.Automations)] },
      {
        path: 'automations/create',
        name: t('routes.automations'),
        jsx: <Automations type="create"></Automations>,
        guard: [guards.accessGuard(ModuleCodes.Automations)],
        fallback: 'automations',
      },
      {
        path: 'automations/edit/:id',
        name: t('routes.automations'),
        jsx: <Automations type="edit"></Automations>,
        guard: [
          guards.accessGuard(ModuleCodes.Automations),
          guards.accessGuard({ code: ModuleCodes.Automations, action: ModuleActions.Automations.View }),
        ],
        fallback: 'automations',
      },

      { path: 'parents', name: t('routes.parents'), component: Parents, guard: [guards.accessGuard(ModuleCodes.Parents)] },
      {
        path: 'parents/create',
        name: t('routes.parents'),
        jsx: <Parents type="create"></Parents>,
        guard: [
          guards.accessGuard(ModuleCodes.Parents),
          guards.accessGuard({ code: ModuleCodes.Parents, action: ModuleActions.Parents.Create }),
          guards.operationGuard([OperationTypes.Default, OperationTypes.School]),
        ],
        fallback: 'parents',
      },
      {
        path: 'parents/edit/:id',
        name: t('routes.parents'),
        jsx: <Parents type="edit"></Parents>,
        guard: [
          guards.accessGuard(ModuleCodes.Parents),
          guards.accessGuard({ code: ModuleCodes.Parents, action: ModuleActions.Parents.Edit }),
          guards.operationGuard([OperationTypes.Default, OperationTypes.School]),
        ],
        fallback: 'parents',
      },

      { path: 'customRules', name: t('routes.customRules'), component: CustomRules, guard: [guards.accessGuard(ModuleCodes.CustomRules)] },
      {
        path: 'customRules/create',
        name: t('routes.customRules'),
        jsx: <CustomRules type="create"></CustomRules>,
        guard: [guards.accessGuard(ModuleCodes.CustomRules)],
        fallback: 'customRules',
      },
      {
        path: 'customRules/edit/:id',
        name: t('routes.customRules'),
        jsx: <CustomRules type="edit"></CustomRules>,
        guard: [
          guards.accessGuard(ModuleCodes.CustomRules),
          guards.accessGuard({ code: ModuleCodes.CustomRules, action: ModuleActions.CustomRules.View }),
        ],
        fallback: 'customRules',
      },
      {
        path: 'customRules/passengerPreferance/advaced/create',
        name: t('routes.customRules'),
        jsx: <CustomRules type="passengerPreferanceAdvacedCreate"></CustomRules>,
        guard: [
          guards.accessGuard(ModuleCodes.CustomRules),
          guards.accessGuard({ code: ModuleCodes.CustomRules, action: ModuleActions.CustomRules.View }),
        ],
        fallback: 'customRules',
      },
      {
        path: 'facilities',
        name: t('routes.facilities'),
        component: LocationFacilityTypes,
        guard: [guards.accessGuard(ModuleCodes.Locations)],
      },
      {
        path: 'facilities/create',
        name: t('routes.facilities'),
        jsx: <LocationFacilityTypes type="create"></LocationFacilityTypes>,
        guard: [guards.accessGuard(ModuleCodes.Locations)],
        fallback: 'facilities',
      },
      {
        path: 'facilities/edit/:id',
        name: t('routes.facilities'),
        jsx: <LocationFacilityTypes type="edit"></LocationFacilityTypes>,
        guard: [guards.accessGuard(ModuleCodes.Locations)],
        fallback: 'facilities',
      },
      {
        path: 'roi-simulation',
        name: t('routes.roiSimulation'),
        component: RoiSimulation,
        guard: [() => !hideRoiSimulation],
      },
      {
        path: 'feedbacks',
        name: t('routes.feedbacks'),
        component: Feedbacks,
        guard: [guards.accessGuard(ModuleCodes.Feedbacks)],
      },
    ];
  }, [t, fallback, unknownPath, isCompanyAdmin, hideRoiSimulation]);

  return routes;
};

export const useRoutes = (): Route[] => {
  const perm = usePerm();
  const authenticatedRoutes = useAuthenticatedRoutes();

  const routes: Route[] = useMemo(() => {
    return [
      { path: '/*', component: Auth, guard: [guards.guestGuard] },
      { path: '/signup', component: Signup, guard: [guards.guestGuard] },
      { path: '/resetpw', component: ResetPassword, guard: [guards.guestGuard] },
      { path: '/forgot_password', component: ForgotPassword, guard: [guards.guestGuard] },
      { path: '/*', component: Layout, guard: [guards.authGuard], children: authenticatedRoutes },
    ];
  }, [perm]);

  return routes;
};

export type Route = ComponentRoute | JSXRoute;

export default useRoutes;
